import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { ChevronLeft, ChevronRight, CalendarCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

function pad(n) { return String(n).padStart(2, '0'); }

export default function StudentAttendance() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/student/attendance?month=${month}&year=${year}`, { headers: getAuthHeaders() })
      .then(r => { setRecords(r.data.records); setHolidays(r.data.holidays); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build lookups
  const statusMap = {};
  records.forEach(r => { statusMap[r.date] = r.status; });
  const holidayDates = new Set(holidays.map(h => h.date));

  const calDays = getCalendarDays(year, month);
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  // Stats
  const presentDays = records.filter(r => r.status === 'present').length;
  const absentDays = records.filter(r => r.status === 'absent').length;
  const totalClasses = presentDays + absentDays;
  const pct = totalClasses > 0 ? Math.round((presentDays / totalClasses) * 100) : null;

  const getDayStatus = (d) => {
    if (!d) return null;
    const dateStr = `${year}-${pad(month)}-${pad(d)}`;
    if (holidayDates.has(dateStr)) return 'holiday';
    if (statusMap[dateStr] === 'present') return 'present';
    if (statusMap[dateStr] === 'absent') return 'absent';
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">My Record</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Attendance</h1>
        <p className="text-[#57534E] font-body mt-1">See how consistently you've been showing up!</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-green-50 rounded-2xl p-4 text-center">
          <p className="font-heading text-2xl font-semibold text-green-600">{presentDays}</p>
          <p className="text-xs text-green-700 font-body mt-0.5">Present</p>
        </div>
        <div className="bg-red-50 rounded-2xl p-4 text-center">
          <p className="font-heading text-2xl font-semibold text-red-500">{absentDays}</p>
          <p className="text-xs text-red-600 font-body mt-0.5">Absent</p>
        </div>
        <div className="bg-[#FFEDD5] rounded-2xl p-4 text-center">
          <p className="font-heading text-2xl font-semibold text-[#D97706]">{pct !== null ? `${pct}%` : '—'}</p>
          <p className="text-xs text-[#D97706] font-body mt-0.5">Rate</p>
        </div>
      </div>

      {/* Calendar card */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-card border border-[#E7E5E4]/50">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-5">
          <button onClick={prevMonth} data-testid="prev-month-button"
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[#FFFAF0] text-[#57534E] transition-colors">
            <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <h2 className="font-heading text-base font-semibold text-[#292524]">
            {MONTHS[month - 1]} {year}
          </h2>
          <button onClick={nextMonth} data-testid="next-month-button"
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-[#FFFAF0] text-[#57534E] transition-colors">
            <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAYS.map(d => (
            <div key={d} className="text-center text-xs font-body font-medium text-[#A8A29E] py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {calDays.map((d, i) => {
              const status = getDayStatus(d);
              const dateStr = d ? `${year}-${pad(month)}-${pad(d)}` : null;
              const isToday = dateStr === todayStr;
              return (
                <div key={i} className="flex items-center justify-center py-1">
                  {d ? (
                    <div
                      data-testid={`calendar-day-${dateStr}`}
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-body font-medium transition-all
                        ${status === 'present' ? 'bg-green-500 text-white' : ''}
                        ${status === 'absent' ? 'bg-red-100 text-red-600' : ''}
                        ${status === 'holiday' ? 'bg-[#F59E0B] text-white' : ''}
                        ${!status && isToday ? 'ring-2 ring-[#F59E0B] text-[#F59E0B] font-semibold' : ''}
                        ${!status && !isToday ? 'text-[#292524]' : ''}
                      `}
                    >
                      {d}
                    </div>
                  ) : <div className="w-8 h-8" />}
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-center gap-5 mt-5 pt-4 border-t border-[#E7E5E4]/60 flex-wrap">
          {[
            { color: 'bg-green-500', label: 'Present' },
            { color: 'bg-red-100 border border-red-200', label: 'Absent' },
            { color: 'bg-[#F59E0B]', label: 'Holiday' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-full ${item.color}`} />
              <span className="text-xs text-[#78716C] font-body">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* No data placeholder */}
      {!loading && records.length === 0 && (
        <div className="bg-white rounded-3xl p-8 text-center shadow-card border border-[#E7E5E4]/50">
          <CalendarCheck className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-heading font-medium text-[#292524]">No attendance records yet</p>
          <p className="text-[#78716C] text-sm font-body mt-1">Your attendance for this month will appear here once your trainer marks it.</p>
        </div>
      )}
    </div>
  );
}
